import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Boxes, Users as UsersIcon, Activity, LogOut, UserCog, Stethoscope, Cpu, Headphones, KeyRound, SlidersHorizontal, ClipboardList, BookOpen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { NEURO_LOGO_URL } from "@/lib/brand";
import NotificationBell from "@/components/NotificationBell";

const adminNav = [
  { to: "/admin/simulations", label: "Simulations", icon: Boxes, tid: "nav-simulations" },
  { to: "/admin/headsets", label: "Headsets", icon: Cpu, tid: "nav-admin-headsets" },
  { to: "/admin/users", label: "Therapists", icon: UserCog, tid: "nav-users" },
  { to: "/admin/telemetry", label: "Analytics", icon: Activity, tid: "nav-telemetry" },
  { to: "/admin/structure", label: "Structure", icon: SlidersHorizontal, tid: "nav-structure" },
  { to: "/admin/device-api", label: "Device API", icon: KeyRound, tid: "nav-device-api" },
];

const clinicianNav = [
  { to: "/roster", label: "Patient Roster", icon: Stethoscope, tid: "nav-roster" },
  { to: "/headsets", label: "My Headsets", icon: Headphones, tid: "nav-headsets" },
  { to: "/modules", label: "Activity guide", icon: BookOpen, tid: "nav-guide" },
  { to: "/therapies", label: "Therapies", icon: ClipboardList, tid: "nav-therapies" },
  { to: "/telemetry", label: "Analytics", icon: Activity, tid: "nav-clinician-telemetry" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Admins get everything a therapist has, in addition to the admin pages.
  // Analytics is omitted for admins because the admin Telemetry page already covers it.
  const groups = user?.role === "admin"
    ? [
        { title: "Administration", items: adminNav },
        { title: "Clinical", items: clinicianNav.filter((n) => n.to !== "/telemetry") },
      ]
    : [{ title: "Clinical", items: clinicianNav }];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="sidebar-shell relative w-64 flex-shrink-0 text-slate-100 flex flex-col">
        <div className="relative z-10 px-6 pt-6 pb-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img src={NEURO_LOGO_URL} alt="NeuroRecoVR" className="h-10 w-10 rounded-lg" />
            <div>
              <div className="font-display text-base font-bold tracking-tight leading-tight">NeuroRecoVR</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-300/70">Console</div>
            </div>
            <div className="ml-auto"><NotificationBell /></div>
          </div>
        </div>

        <nav className="relative z-10 flex-1 py-5 px-3 space-y-1 overflow-y-auto">
          {groups.map((g, gi) => (
            <div key={g.title} className={gi > 0 ? "pt-4 space-y-1" : "space-y-1"}>
          <div className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-300/60">
            {g.title}
          </div>
          {g.items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.tid}
              className={({ isActive }) =>
                cn(
                  "relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all",
                  isActive
                    ? "bg-white/10 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]"
                    : "text-slate-200/80 hover:bg-white/5 hover:text-white"
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r bg-gradient-to-b from-cyan-400 to-fuchsia-500" />
                  )}
                  <n.icon className="h-4 w-4" strokeWidth={1.75} />
                  {n.label}
                </>
              )}
            </NavLink>
          ))}
            </div>
          ))}
        </nav>

        <div className="relative z-10 border-t border-white/10 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-full bg-brand-gradient grid place-items-center text-sm font-semibold text-white">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name || user?.email}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-300/70">{user?.role}</div>
            </div>
          </div>
          <Button
            variant="ghost"
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full justify-start text-slate-200/80 hover:text-white hover:bg-white/5"
          >
            <LogOut className="h-4 w-4 mr-2" strokeWidth={1.75} /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
